import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Truck, ClipboardList, Send, Save, Store, BadgeCheck, Star, Plus, X as XIcon } from "lucide-react";

export default function CompanyDashboard() {
  const { user } = useAuth();
  const nav = useNavigate();
  const [company, setCompany] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [openBriefs, setOpenBriefs] = useState([]);
  const [myQuotes, setMyQuotes] = useState([]);
  const [form, setForm] = useState({
    name: "", description: "", phone: "", email: "", website: "",
    hourly_rate: "", service_areas: [], services: [],
  });
  const [newArea, setNewArea] = useState("");
  const [newService, setNewService] = useState("");

  useEffect(() => {
    if (user && user.user_type !== "removal_company") {
      nav("/dashboard", { replace: true });
      return;
    }
    Promise.all([
      api.get("/my-company").then(({ data }) => data.company).catch(() => null),
      api.get("/briefs?status=open&limit=15").then(({ data }) => data.items || []).catch(() => []),
      api.get("/my-quotes").then(({ data }) => data.items || []).catch(() => []),
    ]).then(([co, briefs, quotes]) => {
      setCompany(co);
      if (co) {
        setForm({
          name: co.name || "",
          description: co.description || "",
          phone: co.phone || "",
          email: co.email || "",
          website: co.website || "",
          hourly_rate: co.hourly_rate ?? "",
          service_areas: co.service_areas || [],
          services: co.services || [],
        });
      }
      // Filter out briefs where I've already quoted
      const quotedIds = new Set(quotes.map((q) => q.brief_id));
      setOpenBriefs(briefs.filter((b) => !quotedIds.has(b.brief_id)));
      setMyQuotes(quotes);
    }).finally(() => setLoading(false));
  }, [user, nav]);

  const addChip = (kind) => {
    const v = (kind === "area" ? newArea : newService).trim();
    if (!v) return;
    if (kind === "area") { setForm((f) => ({ ...f, service_areas: [...new Set([...f.service_areas, v])] })); setNewArea(""); }
    else { setForm((f) => ({ ...f, services: [...new Set([...f.services, v])] })); setNewService(""); }
  };
  const removeChip = (kind, v) => {
    if (kind === "area") setForm((f) => ({ ...f, service_areas: f.service_areas.filter((x) => x !== v) }));
    else setForm((f) => ({ ...f, services: f.services.filter((x) => x !== v) }));
  };

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = {
        name: form.name,
        description: form.description,
        phone: form.phone,
        email: form.email,
        website: form.website || null,
        hourly_rate: form.hourly_rate === "" ? null : Number(form.hourly_rate),
        service_areas: form.service_areas,
        services: form.services,
      };
      if (company) {
        const { data } = await api.patch(`/removal-companies/${company.company_id}`, payload);
        setCompany(data);
        toast.success("Company profile saved");
      } else {
        const { data } = await api.post("/removal-companies", payload);
        setCompany(data);
        toast.success("Company profile created — pending admin approval");
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to save");
    } finally { setSaving(false); }
  };

  if (loading) return <div className="mx-auto max-w-7xl px-6 py-16 text-center text-muted-foreground">Loading company dashboard…</div>;

  if (!company) {
    return (
      <div className="mx-auto max-w-3xl px-6 lg:px-10 py-14">
        <div className="overline text-safety">Removal firm</div>
        <h1 className="mt-2 font-display font-black text-4xl tracking-tighter text-forest">Set up your company profile</h1>
        <p className="mt-3 text-muted-foreground">Fill in your details below. Once submitted, admins will approve you and businesses can find you in the directory.</p>
        <form onSubmit={save} className="mt-8 border border-border bg-white p-6 lg:p-8 space-y-5" data-testid="company-create-form">
          <CompanyFormFields
            form={form}
            setForm={setForm}
            newArea={newArea}
            setNewArea={setNewArea}
            newService={newService}
            setNewService={setNewService}
            addChip={addChip}
            removeChip={removeChip}
            required
          />
          <div className="pt-4 border-t border-border flex justify-end">
            <Button type="submit" disabled={saving} data-testid="company-create-submit" className="rounded-none bg-forest hover:bg-forest-800 text-white h-12 px-8">
              <Save className="w-4 h-4 mr-2" /> {saving ? "Creating…" : "Create company profile →"}
            </Button>
          </div>
        </form>
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-7xl px-6 lg:px-10 py-10">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <div className="overline text-safety">Removal firm</div>
          <h1 className="mt-2 font-display font-black text-4xl tracking-tighter text-forest">{company.name}</h1>
          <div className="mt-2 flex flex-wrap gap-2 items-center">
            {company.is_approved ? (
              <Badge className="rounded-none bg-forest text-white overline">Approved</Badge>
            ) : (
              <Badge className="rounded-none bg-safety text-white overline">Pending approval</Badge>
            )}
            {company.owner_is_verified && (
              <Badge className="rounded-none bg-forest text-white overline inline-flex items-center gap-1">
                <BadgeCheck className="w-3 h-3" /> Verified
              </Badge>
            )}
            {company.rating > 0 && (
              <span className="inline-flex items-center gap-1 text-sm">
                <Star className="w-4 h-4 fill-safety text-safety" /> {company.rating.toFixed(1)} ({company.review_count})
              </span>
            )}
          </div>
        </div>
        <Button asChild variant="outline" className="rounded-none border-forest text-forest hover:bg-forest hover:text-white h-11" data-testid="company-view-public">
          <Link to={`/removal-companies/${company.company_id}`}><Store className="w-4 h-4 mr-2" /> View public page</Link>
        </Button>
      </div>

      {/* Stats */}
      <div className="mt-8 grid grid-cols-1 sm:grid-cols-3 gap-4">
        <Stat n={myQuotes.length} l="Quotes sent" />
        <Stat n={myQuotes.filter((q) => q.brief_status === "open").length} l="Quotes on live requests" />
        <Stat n={openBriefs.length} l="Open requests available" />
      </div>

      <div className="mt-10 grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Open clearance requests to quote on */}
        <section className="border border-border bg-white" data-testid="company-open-briefs">
          <div className="p-5 border-b border-border flex items-center justify-between">
            <div className="inline-flex items-center gap-2 font-display text-xl tracking-tight text-forest">
              <ClipboardList className="w-5 h-5 text-safety" /> Live requests to quote on
            </div>
            <Link to="/briefs" className="text-xs overline text-safety hover:text-forest">All requests →</Link>
          </div>
          {openBriefs.length === 0 ? (
            <div className="p-8 text-center text-sm text-muted-foreground">No open requests available right now. Check back soon.</div>
          ) : (
            <div className="divide-y divide-border">
              {openBriefs.slice(0, 8).map((b) => (
                <Link key={b.brief_id} to={`/briefs/${b.brief_id}`} data-testid={`open-brief-${b.brief_id}`} className="block p-4 hover:bg-sage-light transition-colors">
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0">
                      <div className="font-medium text-sm truncate">{b.title}</div>
                      <div className="mt-1 text-xs text-muted-foreground flex gap-3">
                        <span>{b.location}</span>
                        <span>{b.approx_items}</span>
                      </div>
                    </div>
                    <div className="flex flex-col items-end gap-1 shrink-0">
                      {b.is_featured && <Badge className="rounded-none bg-safety text-white overline text-[9px]">Boosted</Badge>}
                      <Send className="w-4 h-4 text-forest" />
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          )}
        </section>

        {/* My submitted quotes */}
        <section className="border border-border bg-white" data-testid="company-my-quotes">
          <div className="p-5 border-b border-border">
            <div className="inline-flex items-center gap-2 font-display text-xl tracking-tight text-forest">
              <Truck className="w-5 h-5 text-safety" /> My quotes
            </div>
          </div>
          {myQuotes.length === 0 ? (
            <div className="p-8 text-center text-sm text-muted-foreground">You haven't quoted on any requests yet.</div>
          ) : (
            <div className="divide-y divide-border">
              {myQuotes.slice(0, 8).map((q) => (
                <Link key={q.quote_id} to={`/briefs/${q.brief_id}`} data-testid={`my-quote-${q.quote_id}`} className="block p-4 hover:bg-sage-light transition-colors">
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0">
                      <div className="font-medium text-sm truncate">{q.brief_title || q.brief_id}</div>
                      <div className="mt-1 text-xs text-muted-foreground">
                        {q.brief_location} · {new Date(q.created_at).toLocaleDateString("en-GB")}
                      </div>
                    </div>
                    <div className="text-right shrink-0">
                      <div className="font-display text-lg tabular-nums text-forest">£{Number(q.amount).toFixed(0)}</div>
                      <Badge className={`rounded-none overline text-[9px] mt-1 ${q.brief_status === "open" ? "bg-forest text-white" : "bg-muted text-forest"}`}>
                        {q.brief_status || "sent"}
                      </Badge>
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          )}
        </section>
      </div>

      {/* Manage services */}
      <form onSubmit={save} className="mt-10 border border-border bg-white p-6 lg:p-8 space-y-5" data-testid="company-profile-form">
        <div className="flex items-center justify-between border-b border-border pb-4">
          <div className="font-display text-2xl tracking-tight text-forest">Manage services & profile</div>
          <Button type="submit" disabled={saving} data-testid="company-save-btn" className="rounded-none bg-forest hover:bg-forest-800 text-white h-11 px-6">
            <Save className="w-4 h-4 mr-2" /> {saving ? "Saving…" : "Save changes"}
          </Button>
        </div>

        <CompanyFormFields
          form={form}
          setForm={setForm}
          newArea={newArea}
          setNewArea={setNewArea}
          newService={newService}
          setNewService={setNewService}
          addChip={addChip}
          removeChip={removeChip}
        />
      </form>
    </div>
  );
}

function CompanyFormFields({ form, setForm, newArea, setNewArea, newService, setNewService, addChip, removeChip, required = false }) {
  const star = required ? " *" : "";
  return (
    <>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <Field label={`Company name${star}`}>
          <Input required={required} value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="company-field-name" className="rounded-none h-11" />
        </Field>
        <Field label={`Public email${star}`}>
          <Input required={required} type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="company-field-email" className="rounded-none h-11" />
        </Field>
        <Field label={`Phone${star}`}>
          <Input required={required} value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} data-testid="company-field-phone" className="rounded-none h-11" />
        </Field>
        <Field label="Website">
          <Input value={form.website} onChange={(e) => setForm({ ...form, website: e.target.value })} placeholder="https://…" data-testid="company-field-website" className="rounded-none h-11" />
        </Field>
        <Field label="Hourly rate (£)">
          <Input type="number" step="0.01" value={form.hourly_rate} onChange={(e) => setForm({ ...form, hourly_rate: e.target.value })} data-testid="company-field-rate" className="rounded-none h-11" />
        </Field>
      </div>
      <Field label={`Description${star}`}>
        <Textarea required={required} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} rows={4} data-testid="company-field-desc" className="rounded-none" />
      </Field>
      <ChipEditor
        label="Service areas"
        items={form.service_areas}
        value={newArea}
        onChange={setNewArea}
        onAdd={() => addChip("area")}
        onRemove={(v) => removeChip("area", v)}
        placeholder="e.g. London, Manchester"
        testIdPrefix="area"
      />
      <ChipEditor
        label="Services offered"
        items={form.services}
        value={newService}
        onChange={setNewService}
        onAdd={() => addChip("service")}
        onRemove={(v) => removeChip("service", v)}
        placeholder="e.g. Office clearance, Recycling"
        testIdPrefix="service"
      />
    </>
  );
}

function Stat({ n, l }) {
  return (
    <div className="border border-border bg-white p-6">
      <div className="font-display font-black text-4xl text-forest tabular-nums">{n}</div>
      <div className="overline text-muted-foreground mt-1">{l}</div>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <div className="overline text-muted-foreground mb-2">{label}</div>
      {children}
    </div>
  );
}

function ChipEditor({ label, items, value, onChange, onAdd, onRemove, placeholder, testIdPrefix }) {
  return (
    <div>
      <div className="overline text-muted-foreground mb-2">{label}</div>
      <div className="flex flex-wrap gap-2 mb-2">
        {items.map((v) => (
          <span key={v} className="inline-flex items-center gap-1.5 bg-sage-light border border-border px-3 py-1.5 text-sm">
            {v}
            <button type="button" onClick={() => onRemove(v)} className="hover:text-safety" aria-label={`Remove ${v}`}>
              <XIcon className="w-3.5 h-3.5" />
            </button>
          </span>
        ))}
      </div>
      <div className="flex gap-2">
        <Input value={value} onChange={(e) => onChange(e.target.value)} placeholder={placeholder} data-testid={`company-${testIdPrefix}-input`}
          onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); onAdd(); } }}
          className="rounded-none h-11 flex-1" />
        <Button type="button" onClick={onAdd} data-testid={`company-${testIdPrefix}-add`} className="rounded-none bg-forest hover:bg-forest-800 text-white h-11 px-4">
          <Plus className="w-4 h-4" />
        </Button>
      </div>
    </div>
  );
}
